<template>
  <div>
    <!-- Alerta de cobertura -->
    <b-alert
      v-if="hasModelsWithoutCoverage"
      variant="warning"
      show
      class="mb-2"
    >
      <div class="alert-body d-flex align-items-center justify-content-between">
        <span>
          <feather-icon icon="InfoIcon" class="mr-50" />
          {{ $t('Existem modelos sem tabela DTC de cobertura.') }}
        </span>
        <b-button size="sm" variant="warning" :to="{ name: 'dtc-coverage' }">
          {{ $t('Ver cobertura') }}
        </b-button>
      </div>
    </b-alert>

    <b-card no-body class="mb-0">
      <div class="m-2">
        <!-- Toolbar Superior -->
        <b-row>
          <b-col cols="12" md="6" class="d-flex align-items-center mb-1 mb-md-0">
            <h3 class="mb-0">{{ $t('Catálogo de Tabelas DTC') }}</h3>
          </b-col>
          
          <b-col cols="12" md="6" class="d-flex align-items-center justify-content-end">
            <b-form-input
              v-model="searchQuery"
              class="d-inline-block mr-1"
              :placeholder="$t('Buscar por nome ou descrição...')"
              style="max-width: 250px;"
            />
            <b-button
              variant="primary"
              :to="{ name: 'create-dtc-table' }" 
            >
              <feather-icon icon="PlusIcon" class="mr-50" />
              <span class="text-nowrap">{{ $t('Nova tabela') }}</span>
            </b-button>
          </b-col>
        </b-row>
      </div>

      <!-- Tabela -->
      <b-table
        ref="refDtcListTable"
        class="position-relative"
        :items="fetchTables"
        :fields="tableColumns"
        responsive
        primary-key="id"
        show-empty
        :empty-text="$t('Nenhuma tabela DTC. Crie a primeira e importe os códigos.')"
        hover
        @row-clicked="onRowClick"
        :busy.sync="isLoading"
      >
        <!-- Coluna: Descrição com Tooltip -->
        <template #cell(description)="data">
          <span
            v-b-tooltip.hover
            :title="data.value"
            class="d-inline-block text-truncate"
            style="max-width: 200px;"
          >
            {{ data.value }}
          </span>
        </template>

        <!-- Coluna: Criado em -->
        <template #cell(created)="data">
          {{ formatDate(data.value) }}
        </template>

        <!-- Coluna: Ações -->
        <template #cell(actions)="data">
          <div class="text-nowrap">
            <b-button
              variant="flat-primary"
              class="btn-icon mr-50"
              @click.stop="openTable(data.item.id)"
              v-b-tooltip.hover.top="$t('Abrir')"
            >
              <feather-icon icon="EditIcon" />
            </b-button>
            <b-button
              variant="flat-danger"
              class="btn-icon"
              @click.stop="confirmInactivate(data.item.id)"
              v-b-tooltip.hover.top="$t('Inativar')"
            >
              <feather-icon icon="Trash2Icon" />
            </b-button>
          </div>
        </template>
      </b-table>

      <!-- Paginação -->
      <div class="mx-2 mb-2">
        <b-row>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start">
            <span class="text-muted">{{ $t('Mostrando') }} {{ dataMeta.from }} {{ $t('a') }} {{ dataMeta.to }} {{ $t('de') }} {{ dataMeta.of }} {{ $t('registros') }}</span>
          </b-col>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
            <b-pagination
              v-model="currentPage"
              :total-rows="totalItems"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormInput, BButton, BTable, BPagination, BAlert, VBTooltip
} from 'bootstrap-vue'
import store from '@/store'
import { ref, onUnmounted } from '@vue/composition-api'
import useDtcList from './useDtcList'
import dtcStoreModule from './dtcStoreModule'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BPagination,
    BAlert
  },
  directives: {
    'b-tooltip': VBTooltip
  },
  data() {
    return {
      // Variável temporária para simular o badge/alerta no topo
      hasModelsWithoutCoverage: true 
    }
  },
  setup() {
    const DTC_APP_STORE_MODULE_NAME = 'app-dtc'

    // Register module
    if (!store.hasModule(DTC_APP_STORE_MODULE_NAME)) {
      store.registerModule(DTC_APP_STORE_MODULE_NAME, dtcStoreModule)
    }

    const {
      fetchTables,
      tableColumns,
      perPage,
      currentPage,
      totalItems,
      dataMeta,
      perPageOptions,
      searchQuery,
      refDtcListTable,
      refetchData,
      isLoading
    } = useDtcList()

    return {
      fetchTables,
      tableColumns,
      perPage,
      currentPage,
      totalItems,
      dataMeta,
      perPageOptions,
      searchQuery,
      refDtcListTable,
      refetchData,
      isLoading
    }
  },
  methods: {
    formatDate(value) {
      if (!value) return ''
      return new Date(value).toLocaleDateString()
    },
    onRowClick(item) {
      this.openTable(item.id)
    },
    openTable(id) {
      // Rota de exemplo - deve ser ajustada para a rota real de edição de DTC
      this.$router.push({ name: 'edit-dtc-table', params: { id } }).catch(() => {})
    },
    confirmInactivate(id) {
      this.$swal({
        title: this.$t('Você tem certeza?'),
        text: this.$t('Ao inativar esta tabela, os modelos vinculados ficarão sem cobertura até que sejam religados a uma nova tabela.'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('Sim, inativar'),
        cancelButtonText: this.$t('Cancelar'),
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.inactivateTable(id)
        }
      })
    },
    inactivateTable(id) {
      store.dispatch('app-dtc/inactivateTable', { id })
        .then(() => {
          this.$swal({
            icon: 'success',
            title: this.$t('Inativada!'),
            text: this.$t('A tabela DTC foi inativada com sucesso.'),
            customClass: {
              confirmButton: 'btn btn-success',
            },
          })
          this.refetchData()
        })
        .catch(error => {
          this.$swal({
            title: this.$t('Erro'),
            text: this.$t('Ocorreu um erro ao tentar inativar a tabela.'),
            icon: 'error',
            customClass: {
              confirmButton: 'btn btn-primary',
            },
          })
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.btn-icon {
  padding: 0.5rem;
}
</style>